/**
 * Phase 206 — AI Financial Strategy Co-Pilot widget.
 *
 * Sits inside the Add/Edit Student modal's Fee & Scholarship card. Calls
 * POST /api/finance/strategy/suggest-discount on a debounced basis whenever
 * `baseFee` or `guardianPhone` settle, and renders a one-click "Apply"
 * recommendation. The server already enforces a per-academy max-scholarship
 * cap, so this component just renders whatever clamped amount comes back.
 */
import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Sparkles, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function SmartDiscountSuggester({
  baseFee,
  guardianPhone,
  studentId,
  onApply,
}) {
  const [suggestion, setSuggestion] = useState(null);
  const [context, setContext] = useState(null);
  const [status, setStatus] = useState(null); // 'success' | 'fallback' | null
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  // Cache the last input signature so we don't re-fire identical requests
  const lastFiredRef = useRef('');

  useEffect(() => {
    const baseNum = parseFloat(baseFee);
    if (!Number.isFinite(baseNum) || baseNum <= 0) {
      setSuggestion(null);
      setContext(null);
      setStatus(null);
      setError(null);
      return;
    }
    // Debounce 600ms so typing 4-5 digits doesn't burn 5 LLM calls
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const sig = `${baseNum}|${(guardianPhone || '').trim()}|${studentId || ''}`;
      if (sig === lastFiredRef.current) return;
      lastFiredRef.current = sig;

      setLoading(true);
      setError(null);
      try {
        const res = await api.post('/finance/strategy/suggest-discount', {
          course_base_fee: baseNum,
          guardian_phone: (guardianPhone || '').trim() || null,
          student_id: studentId || null,
        });
        setSuggestion(res.data?.recommendation || null);
        setContext(res.data?.context || null);
        setStatus(res.data?.status || null);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Co-pilot unavailable');
        setSuggestion(null);
        setContext(null);
        setStatus(null);
      } finally {
        setLoading(false);
      }
    }, 600);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [baseFee, guardianPhone, studentId]);

  const baseNum = parseFloat(baseFee);
  if (!Number.isFinite(baseNum) || baseNum <= 0) return null;

  const formatInr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const fallback = status === 'fallback';

  return (
    <div
      className="mt-2 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50/80 to-violet-50/60 p-3 text-xs"
      data-testid="smart-discount-suggester"
    >
      {loading && (
        <div className="flex items-center gap-2 text-indigo-700 font-medium" data-testid="copilot-loading">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Calculating optimal retention strategy...</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center gap-2 text-amber-700" data-testid="copilot-error">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && suggestion && (
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Sparkles className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
              <span
                className="font-extrabold text-indigo-900 uppercase text-[10px] tracking-wider"
                data-testid="copilot-tier-label"
              >
                AI Strategy: {suggestion.tier_label || 'Optimized Tier'}
              </span>
              {fallback && (
                <span
                  className="ml-1 px-1.5 py-px rounded bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider"
                  data-testid="copilot-fallback-badge"
                >
                  Fallback
                </span>
              )}
              {suggestion.clamped && (
                <span
                  className="ml-1 px-1.5 py-px rounded bg-rose-100 text-rose-800 text-[9px] font-bold uppercase tracking-wider"
                  data-testid="copilot-clamped-badge"
                >
                  Capped
                </span>
              )}
            </div>
            <p
              className="text-slate-700 font-medium leading-snug"
              data-testid="copilot-rationale"
            >
              {suggestion.rationale}
            </p>
            {context && (
              <p className="text-[10px] text-slate-500" data-testid="copilot-context">
                {context.sibling_count > 0
                  ? `${context.sibling_count} sibling(s) detected • `
                  : 'No siblings detected • '}
                cap {context.max_scholarship_pct}% ({formatInr(context.absolute_ceiling)})
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onApply?.(suggestion.recommended_discount_amount)}
            className="shrink-0 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-md shadow-sm transition-colors flex items-center gap-1 disabled:opacity-40"
            disabled={!(suggestion.recommended_discount_amount > 0)}
            data-testid="copilot-apply-btn"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Apply {formatInr(suggestion.recommended_discount_amount)}</span>
          </button>
        </div>
      )}
    </div>
  );
}
